// Redux
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

// Utils
import { LoadingStates, UNAUTHORISED } from "constants/global";

// API
import { getNewUserRoleGroup as APIGetNewUserRoleGroup, getRoles as APIGetRoles, getNewGroupRoles as APIGetNewGroupRoles } from "services/api/roleManagement";

// Types
import type { RootState, RoleManagement } from "redux/types";
import type { ApiNewUserRoleGroupResponse, ApiRoleResponse, ApiErrorResponse } from "services/api/types";

// Defaults
export const initialState: RoleManagement = {
	roleManagement: {
		loading: LoadingStates.DEFAULT,
		roleGroups: {},
		userRoles: [],
		groupRoles: {}
	},
	hasUnaurizedError: false
};

// Thunks

export const getNewUserRoleGroup = createAsyncThunk<ApiNewUserRoleGroupResponse, undefined, { rejectValue: ApiErrorResponse; state: RootState }>(
	"GET_NEW_USER_ROLE_GROUPS",
	async (_, { rejectWithValue }) => {
		try {
			// Call API
			const { data } = await APIGetNewUserRoleGroup();

			return data;
		} catch (error) {
			return rejectWithValue(error as ApiErrorResponse);
		}
	}
);

export const getUserRoles = createAsyncThunk<Array<ApiRoleResponse>, undefined, { rejectValue: ApiErrorResponse; state: RootState }>(
	"GET_ROLES",
	async (_, { rejectWithValue }) => {
		try {
			// Call API
			const { data } = await APIGetRoles();

			return data;
		} catch (error) {
			return rejectWithValue(error as ApiErrorResponse);
		}
	}
);

export const getNewGroupRoles = createAsyncThunk<Array<ApiRoleResponse>, undefined, { rejectValue: ApiErrorResponse; state: RootState }>(
	"GET_NEW_GROUP_ROLES",
	async (_, { rejectWithValue }) => {
		try {
			// Call API
			const { data } = await APIGetNewGroupRoles();

			return data;
		} catch (error) {
			return rejectWithValue(error as ApiErrorResponse);
		}
	}
);

// Helper
const setLoading = (draft: RoleManagement): void => {
	draft.roleManagement.loading = LoadingStates.LOADING;
};

export const removeLoading = (draft: RoleManagement): void => {
	draft.roleManagement.loading = LoadingStates.IDLE;
};

const setAuthTokenError = (draft: RoleManagement, error: any): void => {
	if (error?.return_description === UNAUTHORISED) {
		draft.hasUnaurizedError = true;
	}
};

// Slice
export const roleManagementSlice = createSlice({
	name: "ROLE_MANAGEMENT",
	initialState,
	reducers: {
		resetRoleManagementState: () => {
			return { ...initialState };
		}
	},
	extraReducers: (builder) => {
		builder.addCase(getNewUserRoleGroup.fulfilled, (draft: RoleManagement, { payload }) => {
			draft.roleManagement.roleGroups = payload;
			draft.roleManagement.loading = LoadingStates.IDLE;
		});
		builder.addCase(getNewUserRoleGroup.pending, setLoading);
		builder.addCase(getNewUserRoleGroup.rejected, (draft: RoleManagement, { payload }) => {
			removeLoading(draft);
			setAuthTokenError(draft, payload);
		});

		// Get Roles list
		builder.addCase(getUserRoles.fulfilled, (draft: RoleManagement, { payload }) => {
			draft.roleManagement.userRoles = payload;
			draft.roleManagement.loading = LoadingStates.IDLE;
		});
		builder.addCase(getUserRoles.pending, setLoading);
		builder.addCase(getUserRoles.rejected, (draft: RoleManagement, { payload }) => {
			removeLoading(draft);
			setAuthTokenError(draft, payload);
		});

		// Get new group roles
		builder.addCase(getNewGroupRoles.fulfilled, (draft: RoleManagement, { payload }) => {
			draft.roleManagement.groupRoles = payload;
			draft.roleManagement.loading = LoadingStates.IDLE;
		});
		builder.addCase(getNewGroupRoles.pending, setLoading);
		builder.addCase(getNewGroupRoles.rejected, (draft: RoleManagement, { payload }) => {
			removeLoading(draft);
			setAuthTokenError(draft, payload);
		});
	}
});

// Actions
export const { resetRoleManagementState } = roleManagementSlice.actions;

// Selector
export const newUserRoleGroupsSelector = (state: RootState): ApiNewUserRoleGroupResponse | undefined => state?.roleManagement?.roleManagement?.roleGroups;
export const newGroupRolesSelector = (state: RootState): ApiNewUserRoleGroupResponse | undefined => state?.roleManagement?.roleManagement?.groupRoles;
export const getLoadingState = (state: RootState): LoadingStates => state?.roleManagement?.roleManagement?.loading;
export const userRolesListSelector = (state: RootState): Array<ApiRoleResponse> => state?.roleManagement?.roleManagement?.userRoles;
export const hasTokenErrRoleMgmtSelector = (state: RootState) => state?.roleManagement?.hasUnaurizedError;

// Reducers
export default roleManagementSlice.reducer;
